import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { PhoneApi } from 'entities/Phone'
import { type InboxLayerStore } from 'pages/settings/pages/inboxes/pages/id/store/inboxLayerStore'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { AddSalesmsgNumbersStore } from 'pages/settings/store/newInboxSteps/addSalesmsgNumbersStore'
import { PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'
import { InboxesRoutes } from 'pages/settings/pages/inboxes/route/type'

export enum AssignNumberRoutesSteps {
  AddSalesmsgNumbers = 'add-salesmsg-numbers',
  Payment = 'payment',
}

const titleMap: Record<string, string> = {
  [AssignNumberRoutesSteps.AddSalesmsgNumbers]: 'Add Salesmsg numbers',
  [AssignNumberRoutesSteps.Payment]: 'Payment',
}

export class AssignNumberStepsStore {
  private _currentStep = 1
  private _loading = false
  private readonly _addSalesmsgNumbersStore: AddSalesmsgNumbersStore
  private readonly _paymentStore: PaymentCommonStore
  private _shouldShowPaymentStep = false

  constructor(private _inboxLayerStore: InboxLayerStore, private _entities: IEntitiesStore) {
    this._addSalesmsgNumbersStore = new AddSalesmsgNumbersStore(
      {
        nextClickHandler: this.handleNextStep,
      },
      this._entities
    )
    this._paymentStore = new PaymentCommonStore(this.assignNumbers, this._entities)

    makeAutoObservable(this)
  }

  init = async () => {
    await this._entities.numbersStore.fetchUnassignedNumbers(true)
    this._addSalesmsgNumbersStore.init(this.unassignedNumbers)
    await this._entities.subscriptionStore.init()
  }

  get inboxLayerStore() {
    return this._inboxLayerStore
  }

  get addSalesmsgNumbersStore() {
    return this._addSalesmsgNumbersStore
  }

  get paymentStore() {
    return this._paymentStore
  }

  get unassignedNumbers() {
    return this._entities.numbersStore.unassignedNumbersUser.filter(
      (number) => !(number.isLandline && number.status !== 'verifiedLandline')
    )
  }

  get unassignedNumbersCount() {
    return this.unassignedNumbers.length
  }

  get shouldShowPaymentNumbersList(): boolean {
    return this.addSalesmsgNumbersStore.numbersStore.multiSelectedValues.length > 1
  }

  get loading() {
    return this._loading
  }

  get inboxId() {
    return this._inboxLayerStore.inboxId
  }

  get rootPath() {
    return `${InboxesPath.inboxNumbers(this.inboxId)}/${InboxesRoutes.assignNumber}`
  }

  get isTwilioNumberVendor() {
    return this._entities.organizationStore.isTwilioNumberVendor
  }

  get availableLicensesCount() {
    return this._entities.subscriptionStore.licensesCount
  }

  get selectedNumbersCount() {
    return this._addSalesmsgNumbersStore.numbersStore.multiSelectedValues.length
  }

  get requiredLicensesToBuy() {
    const numbersCount = this.selectedNumbersCount
    const availableLicenses = this.availableLicensesCount

    if (availableLicenses === 0) {
      return numbersCount
    }

    if (availableLicenses < numbersCount) {
      return numbersCount - availableLicenses
    }

    return 0
  }

  get isPaymentStepEnabled() {
    return this.isTwilioNumberVendor && this.requiredLicensesToBuy > 0
  }

  get stepMap() {
    if (this._shouldShowPaymentStep) {
      return {
        1: AssignNumberRoutesSteps.AddSalesmsgNumbers,
        2: AssignNumberRoutesSteps.Payment,
      }
    }
    return {
      1: AssignNumberRoutesSteps.AddSalesmsgNumbers,
    }
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.getCurrentStep
    return step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1
    return allSteps[currentPageIndex + direction]
  }

  getSettingsStep = (path: AssignNumberRoutesSteps) => {
    return this.getStepByPath(path)
  }

  getStepByPath = (routeStep: AssignNumberRoutesSteps) => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === routeStep
    })
    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  nextClickHandler = () => {
    uiStore.changeRoute({
      path: `${this.rootPath}/${this.getNextStep}`,
    })
  }

  handleNextStep = () => {
    if (this.isPaymentStepEnabled) {
      this._shouldShowPaymentStep = true
      this.nextClickHandler()
    } else {
      this.assignNumbers()
    }
  }

  decreaseStep = () => {
    if (this.getCurrentStep === AssignNumberRoutesSteps.Payment) {
      this._shouldShowPaymentStep = false
    }

    uiStore.changeRoute({
      path: `${this.rootPath}/${this.getLastStep}`,
    })
  }

  redirectToNumbers = () => {
    uiStore.changeRoute({
      path: InboxesPath.inboxNumbers(this.inboxId),
    })
  }

  assignNumbers = async () => {
    this.paymentStore.setPaymentError(false)
    if (!this.inboxId) return

    try {
      runInAction(() => {
        this._loading = true
      })

      await PhoneApi.assignInbox(this.inboxId, {
        numberIds: this._addSalesmsgNumbersStore.numbersStore.multiSelectedKeys,
        members: null,
        name: '',
        number: '',
        owner: { id: null },
      })

      await this._inboxLayerStore.init(this.inboxId)
      toastStore.add({
        type: 'success',
        title: 'Numbers assigned successfully',
      })

      this.redirectToNumbers()
    } catch (e) {
      logger.error(e)
      toastStore.add({
        type: 'error',
        title: 'Failed to assign numbers',
      })

      if (e instanceof AxiosError && e.errors.getError('stripe') === 'Your card was declined.') {
        this.paymentStore.setPaymentError(true)
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._addSalesmsgNumbersStore.numbersStore.handleMultiDeselect()
  }
}
